import type { U2000CriteriaStaticOption } from "../../../u2000Core/components/tables/basic/U2000BasicTableList";
import {
  u3139FormatWorkNatureOptionLabel,
  u3139ResolveWorkNatureLabel,
  type U3139WorkNatureItem,
} from "../../U3139Service";

function isInitWorkNatureActive(item: U3139WorkNatureItem): boolean {
  return item.active !== false;
}

function compareWorkNatureLabels(
  a: U3139WorkNatureItem,
  b: U3139WorkNatureItem,
  language: string,
  catalog?: U3139WorkNatureItem[],
): number {
  const lang = language || "fr";
  const labelA = u3139ResolveWorkNatureLabel(a, lang, catalog);
  const labelB = u3139ResolveWorkNatureLabel(b, lang, catalog);
  const cmp = labelA.localeCompare(labelB, lang, { sensitivity: "base" });
  if (cmp !== 0) return cmp;
  const codeA = a.workNatureCode?.trim() ?? "";
  const codeB = b.workNatureCode?.trim() ?? "";
  return codeA.localeCompare(codeB, lang, { sensitivity: "base" });
}

/** Tri alphabétique par libellé traduit (langue active), tie-breaker workNatureCode. BL-0111 */
export function sortU3139WorkNaturesByLabel(
  workNatures: U3139WorkNatureItem[],
  language: string,
): U3139WorkNatureItem[] {
  if (!workNatures.length) return [];
  const lang = language || "fr";
  return [...workNatures]
    .filter((item) => isInitWorkNatureActive(item))
    .sort((a, b) => compareWorkNatureLabels(a, b, lang, workNatures));
}

/** Options ComboBox critère recherche liste U3139 (natures actives initLU uniquement). */
export function buildU3139WorkNatureCriteriaOptions(
  language: string,
  initWorkNatures?: U3139WorkNatureItem[],
): U2000CriteriaStaticOption[] {
  if (!initWorkNatures?.length) return [];
  const lang = language || "fr";
  return sortU3139WorkNaturesByLabel(initWorkNatures, lang).map((item) => ({
    value: item.workNatureCode ?? "",
    label: u3139FormatWorkNatureOptionLabel(item, lang, initWorkNatures),
  }));
}

/** Options critère `secondaryWorkNatures.workNature.id` (valeur = UUID). BL-0694 */
export function buildU3139WorkNatureIdCriteriaOptions(
  language: string,
  initWorkNatures?: U3139WorkNatureItem[],
): U2000CriteriaStaticOption[] {
  if (!initWorkNatures?.length) return [];
  const lang = language || "fr";
  return sortU3139WorkNaturesByLabel(initWorkNatures, lang).map((item) => ({
    value: item.id ?? "",
    label: u3139FormatWorkNatureOptionLabel(item, lang, initWorkNatures),
  }));
}

/** Filtre local pour autocomplete natures secondaires (fiche minute). */
export function u3139FilterWorkNaturesForSecondaryAutocomplete(
  initWorkNatures: U3139WorkNatureItem[],
  excludedIds: Set<string>,
  query: string,
  language: string,
  extraCatalogItems: U3139WorkNatureItem[] = [],
): U3139WorkNatureItem[] {
  const q = query.trim().toLowerCase();
  const merged = [...initWorkNatures];
  for (const extra of extraCatalogItems) {
    const id = extra.id?.trim();
    if (id && !merged.some((w) => w.id === id)) {
      merged.push(extra);
    }
  }
  return sortU3139WorkNaturesByLabel(merged, language).filter((wn) => {
    const id = wn.id?.trim();
    if (!id || excludedIds.has(id)) return false;
    if (!q) return true;
    const label = u3139FormatWorkNatureOptionLabel(wn, language, merged).toLowerCase();
    const code = wn.workNatureCode?.trim().toLowerCase() ?? "";
    return label.includes(q) || code.includes(q);
  });
}
